import { useEffect, useMemo, useRef, useState } from "react";

import axios from "axios";
import { ChartStatus, useChartsContext } from "contexts";
import { withAwsPresetRange } from "entities/charts/eva/requests/wellStatus";
import debounce from "lodash/debounce";
import { WellStatusChartRequestType } from "types";

import { IChartResult } from "models/model";

// Lean Well Status endpoint — NOT the wells/facilities /chart route.
// Both widget charts POST here, discriminated by chartType.
// REACT_APP_CHART_SERVICE already ends in /api/v1 (same base /chart uses).
const wellStatusEndpoint = `${process.env.REACT_APP_CHART_SERVICE}/wellStatusChart`;

const postWellStatusData = async (
  postData: WellStatusChartRequestType,
  signal?: AbortSignal
): Promise<IChartResult> => {
  const { data } = await axios.post<IChartResult>(
    wellStatusEndpoint,
    withAwsPresetRange(postData),
    { signal }
  );
  return data;
};

/**
 * Mirrors usePostRigFracActivityData: 300ms debounce + AbortController
 * supersession, with loading ownership left to the superseding fetch on cancel.
 *
 * Note for callers: a backend failure can arrive as HTTP 200 with
 * `messageType: GeneralError`, so `error` being null does NOT mean the response
 * is good — branch on `data.messageType` as well.
 */
export const usePostWellStatusData = (postData: Nullable<WellStatusChartRequestType>) => {
  const { setStatus } = useChartsContext();
  const [latestData, setLatestData] = useState<IChartResult | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Nullable<Error>>(null);

  const abortControllerRef = useRef<Nullable<AbortController>>(null);
  // Set when an abort happens because a REPLACEMENT fetch is coming. A plain
  // abort (unmount, or a request that resolves to null and is never re-issued)
  // leaves this false, so the aborted fetch still clears `loading` on its way
  // out — otherwise the widget span forever on its loading state with nothing in
  // flight to end it.
  const hasPendingSuccessorRef = useRef(false);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (postData: WellStatusChartRequestType) => {
        if (abortControllerRef.current) {
          hasPendingSuccessorRef.current = true;
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        hasPendingSuccessorRef.current = false;
        let isSuperseded = false;
        try {
          setLoading(true);
          // Clear any previous failure as soon as a new attempt starts. Held
          // until the next SUCCESS, a stale error sat over freshly rendering
          // data — one transient failure while changing a chart-focus field
          // left "Failed to load chart data" pinned over a healthy chart.
          setError(null);
          const data = await postWellStatusData(postData, controller.signal);
          setLatestData(data);
          setError(null);
        } catch (err) {
          if (axios.isCancel(err) || (err as Error)?.name === "AbortError") {
            // Superseded by a newer fetch — leave loading to that fetch. Only
            // when one actually exists; see hasPendingSuccessorRef.
            isSuperseded = hasPendingSuccessorRef.current;
            return;
          }
          setError(err as Error);
        } finally {
          if (!isSuperseded) setLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    if (postData) {
      // A queued call is about to replace whatever is in flight — mark the abort
      // below as a supersession so the loading state is handed over, not dropped.
      hasPendingSuccessorRef.current = true;
      debouncedFetch(postData);
    }
    return () => {
      debouncedFetch.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [postData, debouncedFetch]);

  useEffect(() => {
    setStatus(
      loading ? ChartStatus.Loading : error ? ChartStatus.Error : ChartStatus.Idle
    );
  }, [loading, error, setStatus]);

  return { data: latestData, loading, error };
};
